import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../i18n/context';
import SEO from '../components/SEO';
import { Container, Reveal } from '../components/Reveal';
import ArticleCard from '../components/ArticleCard';
import { fetchArticles } from '../lib/api';
import { DEFAULT_KOLUM_COVER } from '../lib/site';
import { onImgError } from '../../lib/images';

const READ_LABELS = { ms: 'Baca', en: 'Read', zh: '阅读', ta: 'படிக்க', id: 'Baca' };
const LANG_ORDER = ['ms', 'en', 'zh', 'ta', 'id'];
const LANG_LABELS = { ms: 'BM', en: 'EN', zh: '中文', ta: 'தமிழ்', id: 'ID' };

export default function Kolum() {
    const { t, lang } = useLang();
    const [articles, setArticles] = useState(null);
    const [filter, setFilter] = useState('ms');

    useEffect(() => {
        fetchArticles().then(setArticles).catch(() => setArticles([]));
    }, []);

    const langs = useMemo(() => {
        if (!articles) return [];
        const present = new Set(articles.map((a) => a.language));
        return LANG_ORDER.filter((code) => present.has(code));
    }, [articles]);

    const activeFilter = langs.includes(filter) ? filter : (langs.includes('ms') ? 'ms' : langs[0] || 'ms');

    const shown = useMemo(() => {
        if (!articles) return [];
        return articles.filter((a) => a.language === activeFilter);
    }, [articles, activeFilter]);

    const [featured, ...rest] = shown;

    return (
        <>
            <SEO title={t.kolum.metaTitle} description={t.kolum.metaDesc} path="/kolum" lang={lang} />
            <section className="relative overflow-hidden pt-28 md:pt-36" data-testid="kolum-index">
                <div className="pointer-events-none absolute -left-32 -top-24 h-96 w-96 rounded-full bg-peri-200/60" aria-hidden="true" />
                <Container className="relative">
                    <Reveal>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-gold-700">{t.kolum.title}</p>
                        <h1 className="mt-4 max-w-3xl font-serif text-5xl font-semibold leading-tight tracking-tight text-ink md:text-7xl">
                            Coffee With <span className="italic text-gold-700">Dr. Irma</span>
                        </h1>
                        <p className="mt-4 text-[11px] font-semibold uppercase tracking-[0.24em] text-gold-700" data-testid="kolum-tagline">{t.kolum.tagline}</p>
                        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-ink-muted">{t.kolum.sub}</p>
                    </Reveal>

                    {articles && (
                        <Reveal delay={0.1}>
                            <div className="mt-10 flex flex-wrap items-center gap-2" data-testid="kolum-language-filter">
                                <span className="mr-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-ink-subtle">{t.kolum.translate || 'Terjemah'}:</span>
                                {langs.map((code) => (
                                    <button
                                        key={code}
                                        onClick={() => setFilter(code)}
                                        data-testid={`kolum-filter-${code}`}
                                        className={`rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${activeFilter === code ? 'bg-gold-400 text-ink' : 'border border-peri-300 text-ink-muted hover:bg-peri-50'}`}
                                    >
                                        {LANG_LABELS[code] || code}
                                    </button>
                                ))}
                            </div>
                        </Reveal>
                    )}
                </Container>
            </section>

            <section className="py-14 md:py-20">
                <Container>
                    {articles === null && (
                        <div className="grid gap-5 md:grid-cols-3">{[0, 1, 2].map((i) => <div key={i} className="h-72 animate-pulse rounded-3xl bg-peri-100" />)}</div>
                    )}
                    {articles && articles.length === 0 && (
                        <div className="rounded-3xl border border-dashed border-peri-300 p-12 text-center text-ink-muted">{t.kolum.empty}</div>
                    )}
                    {featured && (
                        <Reveal className="mb-8">
                            <div className="grid overflow-hidden rounded-3xl border border-peri-200 bg-white shadow-subtle md:grid-cols-2">
                                <img src={featured.featured_image || DEFAULT_KOLUM_COVER} alt={featured.image_alt || featured.title} onError={onImgError('dental')} className="h-full w-full object-cover" data-testid="kolum-featured-img" />
                                <div className="p-8 md:p-12">
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gold-700">{featured.category} · {featured.language.toUpperCase()}</p>
                                    <Link to={`/dental/${lang}/kolum/${featured.slug}`} data-testid={`kolum-featured-${featured.slug}`}>
                                        <h2 className="mt-4 font-serif text-3xl font-semibold leading-snug text-ink transition-colors hover:text-gold-700 md:text-4xl">{featured.title}</h2>
                                    </Link>
                                    <p className="mt-4 text-[15px] leading-relaxed text-ink-muted">{featured.meta_description}</p>
                                    <p className="mt-6 text-xs text-ink-subtle">{featured.author} · {featured.publish_date}</p>
                                    <Link to={`/dental/${lang}/kolum/${featured.slug}`} className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold-400 px-6 py-3 text-sm font-semibold text-ink hover:bg-gold-500" data-testid="kolum-featured-read">
                                        {READ_LABELS[lang] || 'Read'} →
                                    </Link>
                                </div>
                            </div>
                        </Reveal>
                    )}
                    <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                        {rest.map((a, i) => (
                            <Reveal key={a.slug} delay={Math.min(i * 0.06, 0.3)} className="h-full">
                                <ArticleCard article={a} />
                            </Reveal>
                        ))}
                    </div>
                    {shown.length === 0 && articles && articles.length > 0 && (
                        <div className="rounded-3xl border border-dashed border-peri-300 p-12 text-center text-ink-muted">{t.kolum.empty}</div>
                    )}
                </Container>
            </section>
        </>
    );
}
